import {
  ExternalAccountsTile,
  ExternalPlaidAccount,
  LinkedAccountsTile,
  Maybe,
  TileStatus,
  TileType,
} from '@varo/graphql-schema-types';
import { ExternalAccountsCardProps } from 'features/home/HomeScreen/components/ExternalAccountsCard/ExternalAccountsCard';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import { ListItemProps } from 'shared/components/vxl/ListItem/ListItem';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';

interface LinkedAccountsTileNode extends VXLComponent {
  id: string;
  name: VXLComponentId.EXTERNAL_ACCOUNTS_CARD;
  props: ExternalAccountsCardProps;
}

const linkedAccountsTile = (
  tile: LinkedAccountsTile,
  dependencies: HomeCardDependencies,
): Maybe<LinkedAccountsTileNode> => {
  const { tiles } = tile;

  const childrenTiles = tiles.filter(
    (tile) => tile.tileType === TileType.ExternalAccountsTile,
  ) as ExternalAccountsTile[];

  const accounts = childrenTiles.flatMap((innerTile) => {
    return innerTile.externalAccounts
      ? (innerTile.externalAccounts as ExternalPlaidAccount[])
      : [];
  });

  if (
    tile.status !== TileStatus.Loaded ||
    tile.tileType !== TileType.LinkedAccounts ||
    !childrenTiles ||
    childrenTiles?.length === 0 ||
    accounts?.length === 0
  ) {
    return null;
  }

  const { goToAccount, manageExternalAccount, translatedText } = dependencies;
  const getAccountData = (acc: ExternalPlaidAccount[]): ListItemProps[] => {
    return acc.map((data: ExternalPlaidAccount) => ({
      keyId: data.id,
      primaryCopy: data.institution.name ?? '',
      secondaryCopy: data.accountNumberLast4 ?? '',
      trailingAccessory: {
        id: data.id,
        name: VXLComponentId.LIST_ITEM_TEXT_STACK,
        props: {
          primaryCopy: data.displayBalance?.amount.formatted ?? '',
          primaryNumberOfLines: 1,
          secondaryCopy: data.lastUpdatedCopy ?? '',
        },
      },
      trailingAccessoryContainerStyle: {
        flexShrink: 0,
      },
      leadingAccessory: {
        id: data.id,
        name: VXLComponentId.ICON_AVATAR,
        props: {
          imageSource: { uri: data.iconUrl },
          variant: 'MD',
        },
      },
      isDisabled: false,
      showsChevron: false,
      onPress: () => {
        goToAccount?.(data.id);
      },
      analyticsId: 'individualLinkedAccountClicked',
      customAttributes: {
        institution_name: data.name,
        state: data.status,
      },
    }));
  };

  return {
    id: tile.id,
    name: VXLComponentId.EXTERNAL_ACCOUNTS_CARD,
    props: {
      title: translatedText('linkedAccountTile.sectionTitle'),
      footerText: translatedText('linkedAccountTile.footerTitle'),
      items: getAccountData(accounts),
      onFooterPress: () => {
        manageExternalAccount();
      },
    },
  };
};

export default linkedAccountsTile;
